import { useState } from 'react';
import { colorPrimarioTramite } from '../Documentos/coloresTramite';
import { ChipEstadoCuota, estadoCuota } from './estadoCobro';
import { PAGO_TODO, esPagoTodo, resumenCobro } from './resumenCobro';
import SeccionColapsable from './SeccionColapsable';
import { montoImputado } from './planCuotas';
import {
    AccionesCobro, EnganchadosCobro, FormularioGestionCobro, FormularioPactoCobro, ListaGestionesCobro,
    anularPagoDelCobro, formatoFecha, formatoMoneda,
} from './PanelesCobro';

/**
 * Las secciones de la cobranza de un trámite: un Resumen arriba, que se entiende de un vistazo, y
 * debajo Información del pacto, Plan de cuotas y pagos, y Bitácora, cada una con lo suyo.
 *
 * Es lo que reemplazó a las cuatro pestañas (Datos / Plan de cuotas / Pagos / Bitácora): en el fondo
 * son un solo objeto, la cobranza, y con pestañas había que ir y venir para ver cómo estaba. Plan y
 * pagos, además, se fusionaron en una sola sección: lo que se debe y lo que se pagó se leen juntos, y
 * cada cuota se paga desde su fila. DetalleCobranza las junta con sus modales, y es lo que muestran la
 * ficha del trámite, el modal del cliente y de los listados, y la atención de un turno de cobro. La
 * lógica (guardar el pacto, anular un pago, registrar una gestión, alta y baja) vive en PanelesCobro.
 */

const COLOR = colorPrimarioTramite('cobro');

const ETIQUETA_TIPO_PAGO = { UN_SOLO_PAGO: 'Un solo pago', EN_CUOTAS: 'En cuotas' };
const ETIQUETA_MEDIO = { EFECTIVO: 'Efectivo', TRANSFERENCIA: 'Transferencia', DEPOSITO: 'Depósito', CHEQUE: 'Cheque', OTRO: 'Otro' };

const plural = (cantidad, uno, varios) => `${cantidad} ${cantidad === 1 ? uno : varios}`;

const estiloTabla = { fontSize: '0.9rem' };
const CABECERA_TABLA = { color: '#64748b', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap' };

// ─────────────────────────────── Resumen

function Indicador({ etiqueta, valor, detalle, color = '#1e293b' }) {
    return (
        <div className="col-6 col-md">
            <div className="rounded-4 p-3 h-100" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <div className="text-muted fw-semibold text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.03em' }}>{etiqueta}</div>
                <div className="fw-bold fs-5" style={{ color }}>{valor}</div>
                {detalle && <div className="small text-muted">{detalle}</div>}
            </div>
        </div>
    );
}

/** Lo primero que se lee: cuánto se pactó, cuánto se cobró, cuánto falta y cómo viene el plan. */
export function ResumenCobro({ cobro }) {
    const r = resumenCobro(cobro);
    const saldado = r.saldo !== null && r.saldo <= 0;
    const proximo = r.proximoVencimiento;

    return (
        <div>
            <div className="row g-3">
                {cobro.tipoPago !== 'UN_SOLO_PAGO' && <Indicador etiqueta="Monto pactado" valor={r.montoPactado === null ? 'Sin definir' : formatoMoneda(r.montoPactado)} />}
                {esPagoTodo(cobro)
                    ? <Indicador etiqueta="Pago" valor={PAGO_TODO} color="#166534"
                        detalle={r.totalPagado > 0 ? `Registrado ${formatoMoneda(r.totalPagado)}` : null} />
                    : <Indicador etiqueta="Total pagado" valor={formatoMoneda(r.totalPagado)} />}
                {cobro.tipoPago !== 'UN_SOLO_PAGO' && (
                    <Indicador
                        etiqueta="Saldo pendiente"
                        valor={formatoMoneda(r.saldo)}
                        detalle={saldado ? 'Saldado' : null}
                        color={saldado ? '#166534' : COLOR}
                    />
                )}
                {cobro.tipoPago !== 'UN_SOLO_PAGO' && (
                    <Indicador
                        etiqueta="Cuotas"
                        valor={r.cantidadCuotas === 0 ? 'Sin plan' : String(r.cantidadCuotas)}
                        detalle={r.cantidadCuotas === 0 ? null : `${plural(r.cuotasPagadas, 'pagada', 'pagadas')} · ${formatoMoneda(r.totalCuotas)}`}
                    />
                )}
                {cobro.tipoPago !== 'UN_SOLO_PAGO' && (
                    <Indicador
                        etiqueta="Próximo vencimiento"
                        valor={proximo ? formatoFecha(proximo.fecha) : '—'}
                        detalle={proximo
                            ? `${proximo.vencida ? 'Vencida · ' : ''}Cuota ${proximo.numero} · ${formatoMoneda(proximo.monto)}`
                            : (r.cantidadCuotas > 0 ? 'Sin cuotas pendientes' : null)}
                        color={proximo?.vencida ? '#991b1b' : '#1e293b'}
                    />
                )}
            </div>

            {r.porcentajePagado !== null && !esPagoTodo(cobro) && (
                <div className="mt-3">
                    <div className="d-flex justify-content-between flex-wrap gap-1 small text-muted mb-1">
                        <span>Cobrado el {r.porcentajePagado}% del monto pactado</span>
                        <span>{formatoMoneda(r.totalPagado)} de {formatoMoneda(r.montoPactado)}</span>
                    </div>
                    <div
                        className="progress"
                        style={{ height: 8 }}
                        role="progressbar"
                        aria-label="Porcentaje cobrado del monto pactado"
                        aria-valuenow={r.porcentajePagado}
                        aria-valuemin={0}
                        aria-valuemax={100}
                    >
                        <div className="progress-bar" style={{ width: `${r.porcentajePagado}%`, background: COLOR }}></div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────── Información del pacto

function DatoPacto({ etiqueta, valor, ancho = 'col-sm-6 col-lg-4' }) {
    return (
        <div className={ancho}>
            <div className="text-muted fw-semibold text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.03em' }}>{etiqueta}</div>
            <div className="fw-semibold" style={{ color: '#1e293b' }}>{valor}</div>
        </div>
    );
}

/** El pacto como información (no como un formulario abierto); "Editar" abre los campos. */
/**
 * @param puedeConfigurar en "Editar", la configuración (pago único o en cuotas, haberes, porcentaje,
 *                        monto): administración o un cobro de plan de pago (ver permisosCobro.js). El
 *                        resto del formulario (banco, fechas, observaciones) lo edita cualquiera
 * @param puedeDarDeBaja  la baja del cobro: solo administración
 */
export function SeccionPacto({ cobro, onCambio, puedeConfigurar = true, puedeDarDeBaja = true }) {
    const [editando, setEditando] = useState(false);

    return (
        <SeccionColapsable
            clave="pacto"
            titulo="Información del pacto"
            icono="bi-file-earmark-text"
            acciones={({ abrir }) => !editando && (
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary rounded-pill px-3 fw-semibold"
                    onClick={() => { abrir(); setEditando(true); }}
                >
                    <i className="bi bi-pencil me-1"></i>Editar
                </button>
            )}
        >
            {editando ? (
                <FormularioPactoCobro
                    cobro={cobro}
                    mostrarSaldo={false}
                    puedeConfigurar={puedeConfigurar}
                    onCancelar={() => setEditando(false)}
                    onGuardado={() => { setEditando(false); onCambio?.(); }}
                />
            ) : (
                <>
                    <EnganchadosCobro cobro={cobro} />
                    <div className="row g-3">
                        <DatoPacto etiqueta="Tipo de pago" valor={ETIQUETA_TIPO_PAGO[cobro.tipoPago] || 'Sin definir'} />
                        <DatoPacto etiqueta="Cantidad de haberes" valor={cobro.cantidadHaberes ?? '—'} />
                        <DatoPacto etiqueta="Porcentaje" valor={cobro.conPorcentaje ? `${cobro.porcentaje ?? 30} %` : 'Sin porcentaje'} />
                        {cobro.tipoPago !== 'UN_SOLO_PAGO' && (
                            <DatoPacto etiqueta="Monto total pactado" valor={cobro.montoTotal === null || cobro.montoTotal === undefined ? 'Sin definir' : formatoMoneda(cobro.montoTotal)} />
                        )}
                        <DatoPacto etiqueta="Banco" valor={cobro.banco || '—'} />
                        <DatoPacto etiqueta="Fecha favorable" valor={formatoFecha(cobro.fechaFavorable)} />
                        <DatoPacto etiqueta="Fecha de alta" valor={formatoFecha(cobro.fechaAlta)} />
                        <DatoPacto etiqueta="Observaciones" valor={cobro.observaciones || '—'} ancho="col-12 col-lg-8" />
                    </div>
                    <hr />
                    <AccionesCobro cobro={cobro} onCambio={onCambio} puedeDarDeBaja={puedeDarDeBaja} />
                </>
            )}
        </SeccionColapsable>
    );
}

// ─────────────────────────────── Plan de cuotas y pagos

/**
 * El plan de cuotas y los pagos en una sola sección: son dos caras de lo mismo (lo que se debe y lo
 * que se fue pagando), y separados había que ir y venir entre ambos para saber cómo venía el cobro.
 *
 * Cada cuota pendiente tiene su "Pagar", que abre el detalle de la cuota y recién ahí confirma el
 * pago (ver ModalPagarCuota). Abajo queda el historial de pagos, con "Anular" para los cargados mal.
 * "Registrar pago" sigue en el encabezado para lo que no es de una cuota: la entrega inicial, un pago
 * a cuenta o la cancelación anticipada.
 */
export function SeccionCuotasYPagos({ cobro, onAbrirModalPlan, onAbrirModalPago, onPagarCuota, onCambio }) {
    const cuotas = cobro.cuotas || [];
    const pagos = cobro.pagos || [];
    const r = resumenCobro(cobro);
    const hayPlan = r.cantidadCuotas > 0;

    let resumen = 'Sin plan';
    if (esPagoTodo(cobro)) {
        resumen = PAGO_TODO;
    } else if (hayPlan) {
        resumen = `${plural(r.cantidadCuotas, 'cuota', 'cuotas')} (${plural(r.cuotasPagadas, 'pagada', 'pagadas')}) · Pagado ${formatoMoneda(r.totalPagado)}`;
    } else if (pagos.length > 0) {
        resumen = `Sin plan · Pagado ${formatoMoneda(r.totalPagado)}`;
    }

    return (
        <SeccionColapsable
            clave="cuotas"
            titulo={cobro.tipoPago === 'UN_SOLO_PAGO' ? 'Pagos' : 'Plan de cuotas y pagos'}
            icono="bi-calendar3"
            resumen={resumen}
            acciones={(
                <div className="d-flex gap-2 flex-wrap justify-content-end">
                    <button type="button" className="btn btn-sm btn-outline-secondary rounded-pill px-3 fw-semibold" onClick={onAbrirModalPago}>
                        <i className="bi bi-plus-lg me-1"></i>Registrar pago
                    </button>
                    {cobro.tipoPago !== 'UN_SOLO_PAGO' && (
                        <button type="button" className="btn btn-sm btn-primary rounded-pill px-3 fw-semibold" onClick={onAbrirModalPlan}>
                            <i className="bi bi-calendar-plus me-1"></i>{hayPlan ? 'Reprogramar plan' : 'Crear plan de cuotas'}
                        </button>
                    )}
                </div>
            )}
        >
            {cobro.tipoPago !== 'UN_SOLO_PAGO' && (
                cuotas.length === 0 ? (
                    <p className="text-muted text-center py-3 mb-0">Este cobro todavía no tiene un plan de cuotas.</p>
                ) : (
                <div className="table-responsive">
                    <table className="table table-sm table-hover align-middle mb-0" style={estiloTabla}>
                        <thead>
                            <tr>
                                <th style={CABECERA_TABLA}>Cuota</th>
                                <th style={CABECERA_TABLA}>Vencimiento</th>
                                <th style={CABECERA_TABLA} className="text-end">Importe</th>
                                <th style={CABECERA_TABLA} className="text-end">Pagado</th>
                                <th style={CABECERA_TABLA} className="ps-4">Estado</th>
                                <th style={CABECERA_TABLA}>Observación</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {cuotas.map((c) => {
                                const anulada = estadoCuota(c) === 'anulada';
                                const imputado = montoImputado(c, pagos);
                                return (
                                    <tr key={c.id} style={{ opacity: anulada ? 0.55 : 1 }}>
                                        <td className="fw-semibold" style={{ textDecoration: anulada ? 'line-through' : 'none' }}>#{c.numero}</td>
                                        <td>{formatoFecha(c.fechaVencimiento)}</td>
                                        <td className="text-end fw-semibold">{formatoMoneda(c.monto)}</td>
                                        <td className="text-end" style={{ color: imputado > 0 ? COLOR : '#94a3b8' }}>{imputado > 0 ? formatoMoneda(imputado) : '—'}</td>
                                        <td className="ps-4"><ChipEstadoCuota cuota={c} /></td>
                                        <td className="text-muted">{c.observacion || ''}</td>
                                        <td className="text-end">
                                            {!anulada && !c.pagada && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-primary rounded-pill px-3 fw-semibold"
                                                    aria-label={`Pagar la cuota ${c.numero}`}
                                                    onClick={() => onPagarCuota?.(c)}
                                                >
                                                    <i className="bi bi-cash-coin me-1"></i>Pagar
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                )
            )}

            <div className={cobro.tipoPago !== 'UN_SOLO_PAGO' ? "mt-4" : ""}>
                <div className="d-flex justify-content-between align-items-baseline mb-2">
                    <span className="text-muted fw-semibold text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.03em' }}>Pagos registrados</span>
                    {pagos.length > 0 && (
                        <span className="small text-muted">{plural(pagos.length, 'pago', 'pagos')} · {formatoMoneda(r.totalPagado)}</span>
                    )}
                </div>
                {pagos.length === 0 ? (
                    <p className="text-muted text-center py-2 mb-0">Todavía no hay pagos registrados.</p>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-sm table-hover align-middle mb-0" style={estiloTabla}>
                            <thead>
                                <tr>
                                    <th style={CABECERA_TABLA}>Fecha</th>
                                    <th style={CABECERA_TABLA} className="text-end">Importe</th>
                                    <th style={CABECERA_TABLA} className="ps-4">Medio</th>
                                    <th style={CABECERA_TABLA}>Imputado a</th>
                                    <th style={CABECERA_TABLA}>Observación</th>
                                    <th style={CABECERA_TABLA}>Registró</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {pagos.map((p) => (
                                    <tr key={p.id}>
                                        <td>{formatoFecha(p.fecha)}</td>
                                        <td className="text-end fw-semibold" style={{ color: COLOR }}>{formatoMoneda(p.monto)}</td>
                                        <td className="ps-4">{ETIQUETA_MEDIO[p.medio] || p.medio || 'Sin medio'}</td>
                                        <td>
                                            {p.esEntregaInicial
                                                ? <span className="badge rounded-pill" style={{ background: '#e0f2fe', color: '#0369a1' }}>Entrega inicial</span>
                                                : (p.numeroCuota ? `Cuota ${p.numeroCuota}` : '—')}
                                        </td>
                                        <td className="text-muted">{p.observacion || ''}</td>
                                        <td className="text-muted">{p.usuarioNombre || 'Sistema'}</td>
                                        <td className="text-end">
                                            <button type="button" className="btn btn-sm btn-outline-danger rounded-pill" onClick={() => anularPagoDelCobro(cobro, p.id, onCambio)}>
                                                <i className="bi bi-x-lg me-1"></i>Anular
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </SeccionColapsable>
    );
}

// ─────────────────────────────── Bitácora

/** Las gestiones (llamados, WhatsApp, notas); "Agregar" muestra el formulario, que se cierra al guardar. */
export function SeccionBitacora({ cobro, onCambio }) {
    const [agregando, setAgregando] = useState(false);
    const gestiones = cobro.gestiones || [];

    return (
        <SeccionColapsable
            clave="bitacora"
            titulo="Bitácora"
            icono="bi-journal-text"
            abiertaPorDefecto={false}
            resumen={gestiones.length > 0 ? plural(gestiones.length, 'gestión', 'gestiones') : 'Sin gestiones'}
            acciones={({ abrir }) => !agregando && (
                <button
                    type="button"
                    className="btn btn-sm btn-primary rounded-pill px-3 fw-semibold"
                    onClick={() => { abrir(); setAgregando(true); }}
                >
                    <i className="bi bi-plus-lg me-1"></i>Agregar
                </button>
            )}
        >
            {agregando && (
                <div className="d-flex align-items-start gap-2 mb-3">
                    <div className="flex-grow-1">
                        <FormularioGestionCobro
                            cobro={cobro}
                            className="d-flex gap-2 align-items-start flex-wrap"
                            onGuardado={() => { setAgregando(false); onCambio?.(); }}
                        />
                    </div>
                    <button type="button" className="btn btn-outline-secondary rounded-pill px-3" onClick={() => setAgregando(false)}>Cancelar</button>
                </div>
            )}
            <ListaGestionesCobro gestiones={gestiones} />
        </SeccionColapsable>
    );
}
